/**
 * Sign-in — the whole passwordless surface in one screen.
 *
 * Two states, decided by the URL:
 *  · `/anmelden?token=…`  the mailed link. The token is POSTed (never a GET —
 *    a mail scanner that pre-fetches URLs must not be able to burn it).
 *  · `/anmelden`          the request form.
 *
 * Every failure the backend can answer with — abgelaufen, bereits verwendet,
 * durch einen neueren ersetzt, ungültig — is shown with the backend's own
 * sentence AND the request form directly underneath. A dead end is not an
 * option: the reader always has the next step on the same screen.
 *
 * The honest bit: whether an e-mail actually arrives is a property of the
 * DEPLOYMENT, not of the build. `BAUPILOT_MAILER=console` (the default) writes
 * the link to the server log and a founder relays it; `=smtp` really sends. The
 * screen asks the server which it is — `mail_delivery` on `/health`, the one
 * signal that is readable here, where nobody has a session yet — and prints the
 * sentence that is true. It used to assert the console default unconditionally,
 * which would have told a prospect no mail was coming while it sat in their
 * inbox.
 */
import { useEffect, useRef, useState } from 'react'
import { CircleAlert, KeyRound, LoaderCircle, Mail } from 'lucide-react'

import { api } from '@/api/client'
import { ErrorNote } from '@/components/ErrorNote'
import { Icon } from '@/components/Icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useSession } from '@/features/auth/SessionContext'
import { redeemOnce } from '@/features/auth/redeem'
import { useI18n } from '@/i18n'
import { useAsync } from '@/lib/useAsync'
import { ROUTES, RETURN_PARAM, navigate, safeReturnPath, useRouteMatch } from '@/lib/router'
import type { Health } from '@/api/types'

export function SignInScreen() {
  const { t } = useI18n()
  const route = useRouteMatch()
  const { adopt, status } = useSession()
  const token = route.search.get('token')
  const next = safeReturnPath(route.search.get(RETURN_PARAM))

  const [redeemError, setRedeemError] = useState<string | null>(null)
  const [redeeming, setRedeeming] = useState(Boolean(token))
  const started = useRef(false)

  useEffect(() => {
    if (!token || started.current) return
    started.current = true
    redeemOnce('magic-link', token)
      .then((session) => {
        adopt(session)
        navigate(next ?? ROUTES.home, { replace: true })
      })
      .catch((cause: unknown) => {
        setRedeemError(cause instanceof Error ? cause.message : String(cause))
      })
      .finally(() => setRedeeming(false))
  }, [token, next, adopt])

  // Already signed in and no token to redeem: nothing to do here. Guarded on
  // the route so that a successful redemption, which navigates away while this
  // component is still mounted for a frame, cannot bounce the reader back.
  const onSignInRoute = route.name === 'signIn'
  useEffect(() => {
    if (onSignInRoute && !token && status === 'signedIn') {
      navigate(next ?? ROUTES.home, { replace: true })
    }
  }, [onSignInRoute, token, status, next])

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="font-display text-[32px] leading-tight font-semibold">{t('auth.title')}</h1>
        <p className="text-[15px] text-muted">{t('auth.lead')}</p>
      </section>

      {redeeming ? (
        <p role="status" className="flex items-center gap-2 text-[14px] text-muted">
          <Icon icon={LoaderCircle} className="motion-safe:animate-spin" />
          {t('auth.redeeming')}
        </p>
      ) : null}

      {redeemError ? (
        <div
          role="alert"
          data-testid="magic-link-error"
          className="flex flex-col gap-2 rounded-sm border border-signal-red/30 bg-signal-red-soft px-4 py-3"
        >
          <p className="flex items-center gap-2 text-[14px] font-medium text-signal-red-text">
            <Icon icon={CircleAlert} />
            {t('auth.linkFailedTitle')}
          </p>
          <p className="text-[13px] text-signal-red-text">{redeemError}</p>
          <p className="text-[13px] text-ink-2">{t('auth.linkFailedNext')}</p>
        </div>
      ) : null}

      {!redeeming ? <RequestLinkForm /> : null}
    </div>
  )
}

/**
 * Does this installation put mail on the wire? `null` = not known yet.
 *
 * `/health` is public by design — it is the only thing a signed-out screen can
 * ask — and it answers with a bare boolean. It carries no host, no account and
 * no sender address, so an anonymous caller learns exactly the one fact the
 * copy below depends on and nothing about how the mail is sent.
 *
 * `null` also covers "the probe failed". The screen then says nothing at all
 * about mail: both sentences are wrong in the other deployment, so guessing one
 * is strictly worse than staying quiet — which is the mistake this screen is
 * here to stop making.
 */
function useMailDelivery(): boolean | null {
  const health = useAsync<Health>((signal) => api.health(signal), [])
  return health.data ? health.data.mail_delivery : null
}

/** The request-a-link form. Answers 202 whether or not the address is known. */
export function RequestLinkForm() {
  const { t } = useI18n()
  const mailDelivery = useMailDelivery()
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const [accepted, setAccepted] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSending(true)
    setError(null)
    setAccepted(null)
    try {
      const response = await api.requestMagicLink(email.trim())
      setAccepted(response.message)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSending(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('auth.requestTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)}>
          <label className="flex flex-col gap-1.5 text-[13px] font-medium text-ink-2" htmlFor="signin-email">
            {t('auth.emailLabel')}
          </label>
          <input
            id="signin-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            className="h-10 rounded-sm border border-border-strong bg-white px-3 text-[15px] text-ink placeholder:text-muted-2"
          />
          <Button type="submit" disabled={sending || email.trim() === ''} className="self-start">
            <Icon icon={sending ? LoaderCircle : Mail} className={sending ? 'motion-safe:animate-spin' : ''} />
            {sending ? t('auth.requesting') : t('auth.requestAction')}
          </Button>
        </form>

        {accepted ? (
          <p role="status" className="rounded-sm border border-border bg-accent-soft px-4 py-3 text-[13px] text-ink-2">
            {accepted}
          </p>
        ) : null}

        {error ? <ErrorNote message={error} /> : null}

        {mailDelivery === null ? null : (
          <p
            data-testid="mail-notice"
            data-mail-delivery={String(mailDelivery)}
            className="flex items-start gap-2 rounded-sm border border-border bg-surface px-4 py-3 text-[12px] text-muted"
          >
            <Icon icon={mailDelivery ? Mail : KeyRound} className="mt-0.5" />
            <span>{t(mailDelivery ? 'auth.mailNotice.delivers' : 'auth.mailNotice.logOnly')}</span>
          </p>
        )}
      </CardContent>
    </Card>
  )
}
